"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { FaSearch, FaBars } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { ChevronRight } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { fetchPopularCategoriesAction } from "@/actions/categoryActions";
import { fetchLatestSettingAction } from "@/actions/settingActions";
import type { CategoryData } from "@/types/category";
import { getStoreLogoAlt } from "@/lib/storeLogo";
import { AdRectangle2 } from "@/components/ads/GptAd";

type StoreSuggestion = {
  _id: string;
  name: string;
  slug: string;
  image: string;
  couponCount: number;
};

export default function Header() {
  const defaultAnnouncement = "🎉 Get the latest coupon updates daily – Save more, spend less!";
  const [popularCategories, setPopularCategories] = useState<CategoryData[]>([]);
  const [announcementText, setAnnouncementText] = useState(defaultAnnouncement);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [storeSearchOpen, setStoreSearchOpen] = useState(false);
  const [filteredStores, setFilteredStores] = useState<StoreSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchCacheRef = useRef<Map<string, StoreSuggestion[]>>(new Map());
  const searchRequestIdRef = useRef(0);
  const prefetchedRoutesRef = useRef<Set<string>>(new Set());
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const fullPrefetchKind = "full" as NonNullable<Parameters<typeof router.prefetch>[1]>["kind"];

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/stores", label: "Stores" },
    { href: "/coupons", label: "Coupons" },
    { href: "/categories", label: "Categories" },
    { href: "/blogs", label: "Blogs" },
    { href: "/about-us", label: "About us" },
  ];

  useEffect(() => {
    async function fetchPopularCategories() {
      try {
        const result = await fetchPopularCategoriesAction(10); // Only fetch 10 popular categories
        setPopularCategories(Array.isArray(result?.data) ? result.data : []);
      } catch (err) {
        console.error("Failed to fetch popular categories:", err);
      }
    }
    fetchPopularCategories();
  }, []);

  useEffect(() => {
    async function fetchAnnouncement() {
      try {
        const result = await fetchLatestSettingAction();
        const text = result?.data?.announcementText?.trim();
        setAnnouncementText(text || defaultAnnouncement);
      } catch (err) {
        setAnnouncementText(defaultAnnouncement);
      }
    }
    fetchAnnouncement();
  }, []);

  useEffect(() => {
    const requestId = ++searchRequestIdRef.current;
    const term = searchTerm.trim();
    const cacheKey = term.toLowerCase() || "__popular__";

    if (!term && !storeSearchOpen) {
      setFilteredStores([]);
      setIsSearching(false);
      return;
    }

    const cached = searchCacheRef.current.get(cacheKey);
    if (cached) {
      setFilteredStores(cached);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    setIsSearching(true);

    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/search/stores?${term ? `q=${encodeURIComponent(term)}&` : ""}limit=5`,
          { signal: controller.signal }
        );

        if (!res.ok) {
          if (requestId === searchRequestIdRef.current) setFilteredStores([]);
          return;
        }

        const json = (await res.json()) as { data?: StoreSuggestion[] };
        if (controller.signal.aborted || requestId !== searchRequestIdRef.current) return;
        const next = Array.isArray(json?.data) ? json.data : [];

        // Cache up to ~50 recent queries to avoid repeat network/DB hits.
        const cache = searchCacheRef.current;
        cache.set(cacheKey, next);
        if (cache.size > 50) {
          const firstKey = cache.keys().next().value;
          if (firstKey) cache.delete(firstKey);
        }

        setFilteredStores(next);
      } catch (err) {
        if ((err as any)?.name !== "AbortError" && requestId === searchRequestIdRef.current) {
          console.error("Store search failed:", err);
          setFilteredStores([]);
        }
      } finally {
        if (requestId === searchRequestIdRef.current) setIsSearching(false);
      }
    }, 60);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [searchTerm, storeSearchOpen]);

  useEffect(() => {
    const closeSearchOnOutsideClick = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (desktopSearchRef.current?.contains(target) || mobileSearchRef.current?.contains(target)) return;

      setStoreSearchOpen(false);
      setMobileSearchOpen(false);
    };

    document.addEventListener("pointerdown", closeSearchOnOutsideClick, true);
    document.addEventListener("click", closeSearchOnOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", closeSearchOnOutsideClick, true);
      document.removeEventListener("click", closeSearchOnOutsideClick, true);
    };
  }, []);

  useEffect(() => {
    const prefetchRoute = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest<HTMLAnchorElement>("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;

      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin || destination.pathname === pathname) return;

      const route = `${destination.pathname}${destination.search}`;
      const prefetched = prefetchedRoutesRef.current;
      if (prefetched.has(route)) return;

      if (prefetched.size >= 40) {
        const oldestRoute = prefetched.values().next().value;
        if (oldestRoute) prefetched.delete(oldestRoute);
      }

      prefetched.add(route);
      router.prefetch(route, { kind: fullPrefetchKind });
    };

    document.addEventListener("pointerover", prefetchRoute, { passive: true });
    document.addEventListener("focusin", prefetchRoute);
    document.addEventListener("pointerdown", prefetchRoute, { passive: true });

    return () => {
      document.removeEventListener("pointerover", prefetchRoute);
      document.removeEventListener("focusin", prefetchRoute);
      document.removeEventListener("pointerdown", prefetchRoute);
    };
  }, [pathname, router]);

  useEffect(() => {
    setStoreSearchOpen(false);
    setMobileSearchOpen(false);
    setSearchTerm("");
    setFilteredStores([]);
  }, [pathname]);

  function handleSelectStore(storeSlug: string) {
    setSearchTerm("");
    setFilteredStores([]);
    setStoreSearchOpen(false);
    setMobileSearchOpen(false);
    router.push(`/stores/${storeSlug}`);
  }

  const renderStoreSuggestions = () => {
    if (!storeSearchOpen) return null;

    const title = !searchTerm.trim()
      ? "Popular shops"
      : filteredStores.length === 1
        ? `${filteredStores[0].name} · ${filteredStores[0].couponCount} ${filteredStores[0].couponCount === 1 ? "coupon" : "coupons"}`
        : `${filteredStores.length} stores found`;

    return (
      <div className="absolute left-0 min-[1201px]:left-[-50px] top-full z-50 mt-3 max-h-[min(70vh,28rem)] w-full min-w-[min(22rem,calc(100vw-2rem))] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-3 text-left shadow-xl" role="listbox" aria-label="Store search results">
        <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-purple-700" aria-live="polite">
          {isSearching ? "Searching shops..." : title}
        </p>
        {filteredStores.length > 0 ? (
          <ul className="space-y-1">
            {filteredStores.map((store) => (
              <li key={store._id}>
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => router.prefetch(`/stores/${store.slug}`, { kind: fullPrefetchKind })}
                  onFocus={() => router.prefetch(`/stores/${store.slug}`, { kind: fullPrefetchKind })}
                  onClick={() => handleSelectStore(store.slug)}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-purple-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-700"
                >
                  <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-white">
                    {store.image ? (
                      <Image
                        src={store.image.startsWith("http") ? store.image : `https://www.itscoupons.com${store.image.startsWith("/") ? store.image : `/${store.image}`}`}
                        alt={getStoreLogoAlt(store.slug)}
                        width={40}
                        height={40}
                        sizes="40px"
                        className="h-full w-full object-cover"
                        style={{ width: "100%", height: "100%" }}
                      />
                    ) : (
                      <span className="text-sm font-semibold text-purple-700">{store.name.charAt(0).toUpperCase()}</span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-gray-900">{store.name}</span>
                    <span className="block text-xs text-gray-500">{store.couponCount} {store.couponCount === 1 ? "coupon" : "coupons"}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : !isSearching && searchTerm.trim().length >= 2 ? (
          <p className="px-3 py-4 text-sm text-gray-500">No shops found for “{searchTerm.trim()}”.</p>
        ) : null}
      </div>
    );
  };

  return (
    <header className="w-full">
      {/* Announcement Bar */}
      {showAnnouncement && (
        <div className="bg-purple-800 text-white text-sm sm:text-base text-center py-2 sm:py-3 relative px-4 sm:px-6 lg:px-16">
          {announcementText}
          <button
            onClick={() => setShowAnnouncement(false)}
            aria-label="Close announcement"
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 text-white text-lg sm:text-xl hover:text-gray-300 focus:outline-none"
          >
            <IoMdClose />
          </button>
        </div>
      )}

      {/* Main Header */}
      <div className="bg-white shadow-sm px-4 sm:px-6 lg:px-16 py-4 md:py-6 flex items-center justify-between relative">
        {/* Logo */}
        <Link
          href="/"
          className="absolute left-4 sm:left-6 lg:left-16 top-[55%] -translate-y-1/2"
        >
          <Image
            src="/logos/ITS-Coupons-Logo.png"
            alt="ITS Coupons Logo"
            width={280}
            height={280}
            className="h-20 sm:h-24 md:h-28 w-auto object-contain"
            priority
          />
        </Link>

        {/* Desktop Navigation + Search */}
        <div className="hidden min-[1201px]:flex flex-1 items-center justify-end pl-44 sm:pl-52 md:pl-60">
          <div className="flex items-center w-full max-w-6xl justify-between relative">
            {/* Nav Links */}
            <nav className="flex space-x-6 text-base lg:text-lg font-medium text-gray-700 mx-auto">
              {navLinks.map(({ href, label }) => {
                const linkPath = href.split("?")[0]; // remove query string
                const isActive = pathname === linkPath || (linkPath !== "/" && pathname.startsWith(linkPath));
                
                // Special handling for Categories with dropdown
                if (label === "Categories") {
                  return (
                    <div
                      key={href}
                      className="relative inline-block"
                      onMouseEnter={() => setCategoriesDropdownOpen(true)}
                      onMouseLeave={() => setCategoriesDropdownOpen(false)}
                    >
                      <Link
                        href={href}
                        onMouseEnter={() => router.prefetch(href, { kind: fullPrefetchKind })}
                        onFocus={() => router.prefetch(href, { kind: fullPrefetchKind })}
                        className={`px-1 py-1 inline-block ${isActive
                          ? "text-purple-800 font-semibold underline underline-offset-4"
                          : "text-gray-700 hover:text-purple-700"
                          }`}
                      >
                        {label}
                      </Link>
                      {categoriesDropdownOpen && popularCategories.length > 0 && (
                        <div className="absolute top-full left-0 mt-0.5 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2 max-h-96 overflow-y-auto">
                          {popularCategories.map((category) => (
                            <Link
                              key={category._id}
                              href={`/stores?category=${category.slug}`}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition"
                              onClick={() => setCategoriesDropdownOpen(false)}
                            >
                              {category.name}
                            </Link>
                          ))}
                          <div className="border-t border-gray-200 mt-2 pt-2">
                            <Link
                              href="/categories"
                              className="block px-4 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50 transition"
                              onClick={() => setCategoriesDropdownOpen(false)}
                            >
                              View All Categories →
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={href}
                    href={href}
                    onMouseEnter={() => router.prefetch(href, { kind: fullPrefetchKind })}
                    onFocus={() => router.prefetch(href, { kind: fullPrefetchKind })}
                    className={`px-1 py-1 ${isActive
                      ? "text-purple-800 font-semibold underline underline-offset-4"
                      : "text-gray-700 hover:text-purple-700"
                      }`}
                  >
                    {label}
                  </Link>
                );
              })}

            </nav>

            {/* Search */}
            <div ref={desktopSearchRef} className="relative w-64">
              <input
                type="search"
                placeholder="Search stores..."
                className="h-11 w-full rounded-full border border-gray-300 bg-white py-2 pl-10 pr-10 text-sm outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-100"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setStoreSearchOpen(true);
                }}
                onClick={() => setStoreSearchOpen(true)}
                onFocus={() => setStoreSearchOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setStoreSearchOpen(false);
                }}
                aria-label="Search stores"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-700" aria-hidden="true" />
              {renderStoreSuggestions()}
            </div>
          </div>
        </div>

        {/* Mobile Buttons */}
        <div className="min-[1201px]:hidden flex items-center gap-4 ml-auto">
          {/* Search Button */}
          <button
            onClick={() => {
              const nextOpen = !mobileSearchOpen;
              setMobileSearchOpen(nextOpen);
              setStoreSearchOpen(nextOpen);
            }}
            aria-label="Open search"
            className="text-gray-700 hover:text-purple-700"
          >
            <FaSearch className="w-6 h-6" />
          </button>

          {/* Sidebar Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className="text-gray-700 hover:text-purple-700"
          >
            {mobileMenuOpen ? (
              <IoMdClose className="w-6 h-6" />
            ) : (
              <FaBars className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Search Bar */}
      {mobileSearchOpen && (
        <div className="min-[1201px]:hidden px-4 sm:px-6 lg:px-16 py-3 bg-white border-b border-gray-200 relative z-40">
          <div ref={mobileSearchRef} className="relative mx-auto max-w-xl">
            <input
              type="search"
              placeholder="Search stores..."
              className="block h-12 w-full rounded-full border border-gray-300 bg-white py-2 pl-11 pr-10 text-sm outline-none transition focus:border-purple-600 focus:ring-2 focus:ring-purple-100"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setStoreSearchOpen(true);
              }}
              onClick={() => {
                setMobileSearchOpen(true);
                setStoreSearchOpen(true);
              }}
              onFocus={() => setStoreSearchOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setStoreSearchOpen(false);
              }}
              aria-label="Search stores"
            />
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-700" aria-hidden="true" />
            {renderStoreSuggestions()}
          </div>
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-0 bg-white z-50 transform transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          } min-[1201px]:hidden`}
      >
        <div className="flex flex-col h-full p-6">
          <div className="mb-4 flex justify-center">
            <AdRectangle2 />
          </div>
          <div className="flex items-center justify-between mb-6">
            <Link href="/" onClick={() => setMobileMenuOpen(false)}>
              <Image
                src="/logos/ITS-Coupons-Logo.png"
                alt="ITS Coupons Logo"
                width={280}
                height={280}
                className="h-20 sm:h-24 md:h-28 w-auto object-contain"
                priority
              />
            </Link>

            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
              className="text-gray-700 hover:text-purple-700"
            >
              <IoMdClose className="w-8 h-8" />
            </button>
          </div>

          <nav className="flex flex-col space-y-6 text-lg font-semibold text-gray-800">
            {navLinks.map(({ href, label }) => {
              const linkPath = href.split("?")[0]; // strip query string
              const isActive =
                pathname === linkPath || (linkPath !== "/" && pathname.startsWith(linkPath));
              return (
                <Link
                  key={href}
                  href={href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${isActive ? "text-purple-800 underline underline-offset-4" : "hover:text-purple-700"}`}
                >
                  {label}
                </Link>
              );
            })}

          </nav>
        </div>
      </div>
    </header>
  );
}
